import React, { useState, useMemo } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";

import { Page } from "modules/layout/Page";
import firebase from "firebaseFactory";
import {
  FormSingleFieldRow,
  Form,
  FormInput,
  FormSubmit,
} from "components/Form";
import { useFirebaseFunction } from "hooks";

interface FormData {
  deviceId: string;
  siteId: string;
  unitId: string;
}

interface DeviceDetail {
  deviceId: string;
  siteId: string;
  unitId: string;
  certificates: string[];
}

export const CreateEditDevice = ({
  getDevice = firebase.function("getDevice"),
  createDevice = firebase.function("createDevice"),
  updateDevice = firebase.function("updateDevice"),
}) => {
  const { deviceId } = useParams();
  const history = useHistory();
  const [serverError, setServerError] = useState<string>();
  const { register, handleSubmit, errors, formState, reset } = useForm<
    FormData
  >();
  const { data: deviceDetails, isLoading } = useFirebaseFunction<DeviceDetail>(
    getDevice,
    { deviceId },
    { shouldFetchData: deviceId !== undefined }
  );
  // set form defaults when deviceDetails changes (on load)
  useMemo(() => reset(deviceDetails), [deviceDetails, reset]);

  const onClickSubmit = handleSubmit(async (data: FormData) => {
    try {
      deviceId
        ? await updateDevice.action(data)
        : await createDevice.action(data);
    } catch (ex) {
      setServerError(ex.message);
      return;
    }
    history.push(`/admin/devices/`);
  });

  const deviceIdRegexFromSpec = /^(?!all$|any$|command$|response$|request$|data$|x-)[A-z0-9\-_]{3,32}$/;

  return (
    <Page isLoading={isLoading}>
      <Form onSubmit={onClickSubmit} serverError={serverError}>
        <FormSingleFieldRow>
          <FormInput
            name="deviceId"
            label="Device Id"
            note="You cannot modify this after the device is created"
            readOnly={deviceId}
            required
            register={register}
            errors={errors}
            pattern={RegExp(deviceIdRegexFromSpec)}
          />
        </FormSingleFieldRow>
        <FormSingleFieldRow>
          <FormInput
            name="unitId"
            label="Unit Id"
            required
            register={register}
            errors={errors}
            pattern={RegExp(deviceIdRegexFromSpec)}
          />
        </FormSingleFieldRow>
        <FormSingleFieldRow>
          <FormInput
            name="siteId"
            label="Site Id"
            register={register}
            errors={errors}
            pattern={RegExp(deviceIdRegexFromSpec)}
          />
        </FormSingleFieldRow>
        <FormSubmit
          isLoading={isLoading || formState.isSubmitting}
          label={deviceId ? "Save device" : "Create device"}
        />
      </Form>
      <h2 className="text-xl font-bold leading-tight pt-10 pb-5">
        Certificates
      </h2>
      <ul>
        {deviceDetails ? (
          deviceDetails.certificates.map((certificate, index) => (
            <li key={index}>{certificate}</li>
          ))
        ) : (
          <>Will be listed after creation</>
        )}
      </ul>
    </Page>
  );
};
